import { CaseStudy } from 'types/generated/contentful'
import Meta from 'components/Meta'
import { Header } from 'components/marketing/Header'
import Section from 'components/common/Section'
import RichTextRenderer from 'components/marketing/RichTextRenderer'
import { Marketing } from 'designsystem/Text'
import { CTASection } from 'components/marketing/CTASection'
import { SectionHeader } from 'components/marketing/SectionHeader'
import CaseStudyInfoHero from 'components/marketing/CaseStudyInfoHero'
import IconRenderer, { Icon } from 'components/marketing/IconRenderer'
import NextLink from 'next/link'
import { USE_CASES_SLUG } from 'common/constants'
import { Button } from 'designsystem/Button'
import { Input } from 'designsystem/Input'
import { useForm } from 'react-hook-form'
import { emailRegex } from '../../utils/validation'

const SUBSCRIBE_MUTATION = `
  mutation downloadCaseStudy($email: String!, $slug: String!) {
    downloadCaseStudy(email: $email, slug: $slug)
  }
`

const graphqlDomain =
  process.env.STAGE === 'production'
    ? 'https://stellate-api.stellate.sh'
    : process.env.STAGE === 'staging'
      ? 'https://stellate-api-staging.stellate.dev'
      : '/api'

type CaseStudyPageProps = {
  caseStudy: CaseStudy
  readMoreCases: CaseStudy[]
}

/**
 * NOTE: This component automatically renders an upsell if `publicBlurb` is fetched; otherwise, if `body`
 * is fetched, it will render the full case study.
 */
export default function CaseStudyPage({
  caseStudy,
}: CaseStudyPageProps): JSX.Element {
  const {
    title,
    publicBlurb,
    body,
    metaDescription,
    metaImage,
    quote,
    industry,
    technology,
  } = caseStudy
  const { company } = quote?.author || {}

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string }>()

  const renderFullCaseStudy = !!body
  const richText = renderFullCaseStudy ? body : publicBlurb

  return (
    <div className='bg-grey'>
      <Meta
        title={title}
        description={metaDescription}
        imageSrc={metaImage?.url}
        imageHeight={metaImage?.height}
        imageWidth={metaImage?.width}
      />

      <Header />

      <main className='marketing-page'>
        <Section
          verticalPadding='first'
          className='sm:flex sm:flex-col sm:items-center'
        >
          {company?.logo?.url && company.logo.width && company.logo.height && (
            <img
              src={company.logo.url}
              width={company.logo.width}
              height={company.logo.height}
              alt={company.logo.title || `${company.name} logo` || ''}
              className='h-10 w-auto md:h-16'
            />
          )}

          <Marketing.H1 className='spacer-md'>{title}</Marketing.H1>

          <div className='flex flex-wrap'>
            {industry && (
              <NextLink
                href={`${USE_CASES_SLUG}/${industry.type}/${industry.slug}`}
                className='spacer-sm flex items-center space-x-3 text-red mr-8'
              >
                <IconRenderer
                  icon={industry.slug as Icon}
                  className='h-8 w-auto'
                />
                <span className='font-demibold'>{industry.title}</span>
              </NextLink>
            )}
            {technology && (
              <NextLink
                href={`${USE_CASES_SLUG}/${technology.type}/${technology.slug}`}
                className='spacer-sm flex items-center space-x-3 text-magenta'
              >
                <IconRenderer
                  icon={technology.slug as Icon}
                  className='h-8 w-auto'
                />
                <span className='font-demibold'>{technology.title}</span>
              </NextLink>
            )}
          </div>
        </Section>
        <Section noTopPadding>
          <CaseStudyInfoHero caseStudy={caseStudy} />
        </Section>

        <Section noTopPadding>
          {richText && <RichTextRenderer data={richText} />}
        </Section>

        {!renderFullCaseStudy && (
          <Section
            bgColor='gray'
            noTopPadding
            className='flex flex-col items-start sm:items-center'
          >
            <IconRenderer
              icon='notifications'
              className='h-20 w-auto text-blue'
            />

            <SectionHeader
              title='Get the full story'
              subline='Enter your email below and we’ll send you the full case study right away.'
              className='spacer-sm'
              paddingToContent='small'
            />

            <form
              onSubmit={handleSubmit(({ email }) => {
                fetch(graphqlDomain, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                  },
                  body: JSON.stringify({
                    query: SUBSCRIBE_MUTATION,
                    variables: {
                      email,
                      slug: caseStudy.slug,
                    },
                  }),
                }).then(async (res) => {
                  if (!res.ok) {
                    console.error(res)
                  } else {
                    const { data } = await res.json()
                    window.location.href = data.downloadCaseStudy
                  }
                })
              })}
              className='flex flex-row space-x-8'
            >
              <Input
                className='max-w-full'
                id='email'
                placeholder='Email Address'
                type='text'
                {...register('email', {
                  required: 'Please enter an email',
                  pattern: {
                    message: "Looks like that isn't a valid email",
                    value: emailRegex,
                  },
                })}
                error={errors.email?.message}
              />

              <Button pageType='marketing' color='blue' tabIndex={-1}>
                Submit
              </Button>
            </form>
          </Section>
        )}

        <CTASection
          title='What are you waiting for?'
          subline='Set up Stellate in less than 5 minutes to unlock the full potential of your GraphQL API.'
        />
      </main>
    </div>
  )
}
